// HubSpot UI Extension iframe handshake.
//
// This module is the authoritative source for the signed-postMessage protocol
// that binds the embedded sales workspace to its HubSpot host. It replaces the
// earlier postMessage listener in ./hubspotContext.ts, which did not verify
// event.source and settled only once (any iframe could spoof the context).
//
// WIRE PROTOCOL (version 1)
// =========================
//
// 1. On mount, the workspace posts to window.parent:
//      { type: 'saga.handshake.hello', version: 1 }
//
// 2. HubSpot responds (and may re-emit when the rep navigates between
//    records, so the handler stays attached across the iframe's lifetime):
//      {
//        type: 'hubspot.session',
//        version: 1,
//        token: '<signed JWT>',
//        expiresAt: <unix ms when token stops being valid>,
//        portalId: 245546374,
//        userId:  '<HubSpot user id>',
//        email:   '<rep email>',
//        context: { objectType: 'contact' | 'company' | ..., objectId: '<record id>' },
//        work:    { workId: '<optional work-order id>' } | null
//      }
//
// 3. At 80% of (expiresAt - receivedAt), the workspace posts a refresh
//    request to the parent:
//      { type: 'saga.session.refresh', version: 1 }
//    HubSpot replies with a fresh 'hubspot.session' message or an error:
//      { type: 'hubspot.session.error', version: 1, code: 'REFRESH_FAILED' }
//    If no new session arrives before the current token's expiresAt the
//    handshake flips to 'expired' and the UI shows a visible banner.
//
// VERIFICATION
// ============
// Every inbound MessageEvent is rejected unless:
//   a) event.source === window.parent (closes the spoofing hole flagged by
//      the scoping bot: without this check, any nested iframe inside the
//      HubSpot page could broadcast the message type and win the handshake)
//   b) event.origin matches one of the HubSpot domain patterns below
//   c) event.data looks like one of the known message shapes
//
// The handler stays attached for the lifetime of the iframe. When the rep
// navigates to a different HubSpot record, HubSpot re-emits
// 'hubspot.session' with the new context and the workspace updates in
// place (no remount).

import { getMockHubSpotContext } from './mockContext.js';
import { isWorkspaceRole, roleForUserId, type WorkspaceRole } from './workspace-role.js';
// W44 (2026-10-21): Pattern A. The card opens the iframe with
// ?s=<mint token>; the handshake exchanges it once for a workspace
// bearer and never arms the url-bootstrap timers on that path.
import {
  exchangeFailureMessage,
  exchangeMintToken,
  readMintTokenFromSearch,
  stripMintTokenFromLocation,
  type ExchangeResult,
} from '../api/session-exchange.js';

// --- Protocol constants ---------------------------------------------------

export const MSG = Object.freeze({
  HELLO: 'saga.handshake.hello',
  REFRESH: 'saga.session.refresh',
  SESSION: 'hubspot.session',
  SESSION_ERROR: 'hubspot.session.error',
});

export const PROTOCOL_VERSION = 1 as const;

// Default fraction of the token TTL at which we proactively request a
// refresh. 0.8 keeps a 20% headroom before expiresAt so a slow network does
// not race us to the UI.
export const DEFAULT_REFRESH_FRACTION = 0.8;

// Minimum delay to the refresh timer, so a nearly-expired token at bootstrap
// does not spam the parent in a tight loop. The parent is still expected to
// honour the refresh if we send one, but we hold off at least this long.
const MIN_REFRESH_DELAY_MS = 1_000;

// W22 (2026-10-20): HubSpot's actions.openIframeModal does NOT emit a
// postMessage handshake with the embedded iframe. The W21 build opened
// the workspace through that action with ?portalId=... &contactId=...
// query params, then waited forever for a 'hubspot.session' message that
// never arrives, so the UI stuck on "Opening Sales Workspace..." (what
// Doug read as "blank").
//
// This grace window gives any real postMessage handshake (future HubSpot
// SDK upgrade, or a card-side onMessage bridge) a chance to deliver a
// signed session before we fall back to bootstrapping from the URL
// params the card already put on the iframe URL. The URL-bootstrapped
// session carries a placeholder token the api-server will reject; the
// handshake listener STAYS attached so a real session can still arrive
// and upgrade the context in place.
//
// W23 (2026-10-20): the W22 grace timer did not fire inside the HubSpot
// openIframeModal sandbox (Build #8). Three defensive changes here:
//   1. Grace window reduced from 1500ms to 500ms so a throttled timer in
//      a backgrounded or newly-created cross-origin iframe still fires
//      inside the sandbox's typical first-second budget.
//   2. parseUrlBootstrapSession ALWAYS returns a session now. Missing
//      query params fall through to safe placeholder ids so the UI
//      mounts "something" instead of hanging in pending forever.
//   3. A redundant W23 backup timer at 3000ms re-attempts the bootstrap
//      unconditionally in case the primary timer was deferred.
// Diagnostic logs with '[W23]' prefix emit at every stage so Chrome
// DevTools (and Doug reading the console in the modal via Inspect)
// can see what the handshake did.
const URL_BOOTSTRAP_GRACE_MS = 500;
const URL_BOOTSTRAP_BACKUP_GRACE_MS = 3_000;
const URL_BOOTSTRAP_TOKEN = 'url-bootstrap-no-jwt-pending';
const URL_BOOTSTRAP_TTL_MS = 60 * 60 * 1000;

// W23: default placeholder ids used when the iframe URL carries no
// portalId/contactId. The resulting session identifies the rep as
// 'unknown' and the customer as the empty string, which causes
// ownership-scoped screens to fail closed with "Access denied" but at
// least mounts the chrome and the error page.
const URL_BOOTSTRAP_PLACEHOLDER_PORTAL = 245546374; // 100X Equine portal
const URL_BOOTSTRAP_PLACEHOLDER_USER = 'url-bootstrap-user';
const URL_BOOTSTRAP_PLACEHOLDER_EMAIL = 'url-bootstrap@unknown';
const URL_BOOTSTRAP_PLACEHOLDER_OBJECT_TYPE = 'contact';
const URL_BOOTSTRAP_PLACEHOLDER_OBJECT_ID = '';

function w23log(stage: string, extra?: Record<string, unknown>): void {
  // Diagnostic log channel. Keeping these inline so Doug can grep the
  // console in Chrome DevTools when the modal is open. All '[W23]' lines
  // are safe to leave in production; they emit at most ~6 lines per mount.
  try {
    const g = globalThis as { console?: Console };
    if (g.console && typeof g.console.log === 'function') {
      g.console.log('[W23] ' + stage, extra ?? '');
    }
  } catch {
    /* ignore */
  }
}

// Origins HubSpot serves its embedded UI from. These are the frame ancestor
// origins for a UI Extension iframe. If HubSpot opens the extension from a
// new region, the pattern list grows in review here, not in a hidden config.
const TRUSTED_ORIGIN_PATTERNS: RegExp[] = [
  /^https:\/\/(?:app|app-eu|app-na1|app-na2|app-na3|api|api-eu)\.hubspot\.com$/,
  /^https:\/\/(?:local|localhost)\.hubspot(?:qa)?\.com$/,
  /^https:\/\/[a-z0-9-]+\.hubspotpreview\.com$/,
];

// --- Public types ---------------------------------------------------------

export interface HubSpotSessionContext {
  objectType: string;
  objectId: string;
}

export interface HubSpotWorkContext {
  workId: string;
}

export interface HubSpotSession {
  token: string;
  // Unix ms epoch when the token is no longer valid. Required.
  expiresAt: number;
  portalId: number;
  userId: string;
  email: string;
  context: HubSpotSessionContext;
  // Null when the iframe is opened outside a work order. HubSpot sends this
  // when the extension is launched from a routing surface (e.g. the Current
  // Action card). The brief treats customerId + workId as a pair.
  work: HubSpotWorkContext | null;
  // The millisecond timestamp this process observed when the session arrived.
  receivedAt: number;
  // W22 (2026-10-20): true when this session was synthesized from URL
  // query params because the parent window never delivered a signed
  // handshake message. The api-server will reject writes with such a
  // session's token; the UI can still render read-only context. A real
  // signed session that arrives later REPLACES this one in place.
  urlBootstrapped?: boolean;
  // W37b (2026-10-20): workspace role, mirrored from the api-server's
  // WORKSPACE_MANAGER_USER_IDS rule (auth/workspace-role.ts). The
  // url-bootstrap session derives it from the deep link's userId; a
  // signed payload may carry `role` explicitly, else the same rule
  // applies to its userId. Read by useRep / RequireRole. The server
  // still enforces the role on every manager read.
  role?: WorkspaceRole;
  // W44 (2026-10-21): true when this session came from
  // POST /v1/session/exchange (Pattern A). `token` is then the 60-minute
  // workspace bearer (`ws1.<token>`), identity and role are
  // server-authoritative, and the transports attach NO X-User-* headers
  // (they key those off `urlBootstrapped`, which is never set here).
  sessionExchanged?: boolean;
}

export type HandshakeStatus =
  | 'pending'
  | 'ready'
  | 'refreshing'
  | 'expired'
  | 'error';

export interface HandshakeState {
  status: HandshakeStatus;
  session: HubSpotSession | null;
  // Last error, if any. 'expired' carries the previous session so the UI can
  // display stale context behind the banner.
  error: HandshakeError | null;
}

export interface HandshakeError {
  code:
    | 'HANDSHAKE_INVALID'
    | 'HANDSHAKE_UNTRUSTED_SOURCE'
    | 'HANDSHAKE_UNTRUSTED_ORIGIN'
    | 'REFRESH_FAILED'
    | 'TOKEN_EXPIRED'
    | 'MOCK_UNAVAILABLE'
    // W44: POST /v1/session/exchange refused or failed (used / expired
    // mint token, network). The UI tells the rep to re-open the card.
    | 'SESSION_EXCHANGE_FAILED';
  message: string;
}

export type HandshakeListener = (state: HandshakeState) => void;

export interface HandshakeOptions {
  // Injectable Window for tests. Defaults to the global window.
  windowRef?: Window;
  // When true, skip postMessage and inject the mock session.
  forceMock?: boolean;
  // Clock seam.
  now?: () => number;
  // setTimeout / clearTimeout seams. Default to windowRef's.
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  // Fraction of TTL at which to request a refresh (0 < x < 1).
  refreshFraction?: number;
  // Override for the trusted origin list, used by tests.
  trustedOrigins?: RegExp[];
  // W22: milliseconds to wait for a real postMessage handshake before
  // falling back to URL-param bootstrap. Set to 0 to disable the grace
  // window and bootstrap immediately when URL params are present. Set to
  // Infinity to disable the fallback entirely (tests use Infinity).
  urlBootstrapGraceMs?: number;
  // W22: injectable location.search for tests. Defaults to windowRef.location.search.
  locationSearch?: string;
  // W44: injectable mint-token exchange for tests. Defaults to
  // exchangeMintToken (POST /v1/session/exchange, same origin).
  exchangeImpl?: (token: string) => Promise<ExchangeResult>;
}

export interface Handshake {
  getState(): HandshakeState;
  subscribe(listener: HandshakeListener): () => void;
  teardown(): void;
  // W27 (2026-10-20): replace the record context on the live session in
  // place, e.g. when Finish & Next moves the rep to the next queued
  // contact. Every transport reads the session on each request, so the
  // X-Customer-Id (and X-Work-Id) headers follow the new record without
  // a remount. Returns false when no session is live yet. Optional so
  // test doubles that only implement the three core methods still
  // satisfy the interface.
  setContext?(context: HubSpotSessionContext, work?: HubSpotWorkContext | null): boolean;
}

// --- Internals ------------------------------------------------------------

function isTrustedOrigin(origin: string, patterns: RegExp[]): boolean {
  if (!origin || typeof origin !== 'string') return false;
  return patterns.some((rx) => rx.test(origin));
}

function pickString(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  if (typeof v === 'string' && v.length > 0) return v;
  return null;
}

function pickNumber(obj: Record<string, unknown>, key: string): number | null {
  const v = obj[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

// Pull a HubSpotSession out of a parsed hubspot.session message. Returns
// null if any required field is missing or malformed. The parse is strict:
// we do not fall back on legacy key spellings here, because the authenticated
// transport depends on knowing which exact field the token lives in.
export function parseSessionPayload(
  payload: unknown,
  receivedAt: number,
): HubSpotSession | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const p = payload as Record<string, unknown>;
  const token = pickString(p, 'token');
  const portalId = pickNumber(p, 'portalId');
  const userId = pickString(p, 'userId');
  const email = pickString(p, 'email');
  const expiresAt = pickNumber(p, 'expiresAt');
  const ctxRaw = p['context'];
  if (
    !token ||
    portalId === null ||
    !userId ||
    !email ||
    expiresAt === null ||
    !ctxRaw ||
    typeof ctxRaw !== 'object' ||
    Array.isArray(ctxRaw)
  ) {
    return null;
  }
  const ctx = ctxRaw as Record<string, unknown>;
  const objectType = pickString(ctx, 'objectType');
  const objectId = pickString(ctx, 'objectId');
  if (!objectType || !objectId) return null;
  let work: HubSpotWorkContext | null = null;
  const workRaw = p['work'];
  if (workRaw && typeof workRaw === 'object' && !Array.isArray(workRaw)) {
    const workId = pickString(workRaw as Record<string, unknown>, 'workId');
    if (workId) work = { workId };
  }
  // W37b: an explicit, valid `role` on the payload wins; otherwise the
  // allow-list rule the api-server applies to the JWT's user id.
  const roleRaw = p['role'];
  const role: WorkspaceRole = isWorkspaceRole(roleRaw) ? roleRaw : roleForUserId(userId);
  return {
    token,
    expiresAt,
    portalId,
    userId,
    email,
    context: { objectType, objectId },
    work,
    receivedAt,
    role,
  };
}

// W22: Pull a bootstrap session from URL query params. HubSpot's
// openIframeModal does not emit a handshake postMessage, so without this
// the workspace stalls on 'pending' forever. The card already encodes
// portalId + contactId in the iframe URI; we accept those as a last
// resort when no signed message has arrived in time.
//
// W23 (2026-10-20): ALWAYS return a session. If URL params are missing
// or malformed, synthesize placeholder values. The caller must mount
// SOMETHING instead of hanging in pending; the UI will show "Access
// denied" or similar scope-failure state, which is progress over a
// permanent "Opening Sales Workspace..." spinner.
export function parseUrlBootstrapSession(
  search: string | null | undefined,
  receivedAt: number,
): HubSpotSession {
  let params: URLSearchParams;
  try {
    const safeSearch = typeof search === 'string' ? search : '';
    params = new URLSearchParams(safeSearch.startsWith('?') ? safeSearch.slice(1) : safeSearch);
  } catch {
    params = new URLSearchParams('');
  }
  const portalIdRaw = params.get('portalId');
  const contactId = params.get('contactId');
  const portalIdNum = portalIdRaw ? Number(portalIdRaw) : NaN;
  const portalId = Number.isFinite(portalIdNum) && portalIdNum > 0
    ? portalIdNum
    : URL_BOOTSTRAP_PLACEHOLDER_PORTAL;
  const userId = params.get('userId') || URL_BOOTSTRAP_PLACEHOLDER_USER;
  const email = params.get('email') || URL_BOOTSTRAP_PLACEHOLDER_EMAIL;
  const objectType = params.get('objectType') || URL_BOOTSTRAP_PLACEHOLDER_OBJECT_TYPE;
  const objectId = contactId || URL_BOOTSTRAP_PLACEHOLDER_OBJECT_ID;
  const workId = params.get('workId');
  w23log('parseUrlBootstrapSession', {
    hadPortalId: !!portalIdRaw,
    hadContactId: !!contactId,
    hadUserId: !!params.get('userId'),
    hadEmail: !!params.get('email'),
    searchLen: typeof search === 'string' ? search.length : 0,
  });
  return {
    token: URL_BOOTSTRAP_TOKEN,
    expiresAt: receivedAt + URL_BOOTSTRAP_TTL_MS,
    portalId,
    userId,
    email,
    context: { objectType, objectId },
    work: workId ? { workId } : null,
    receivedAt,
    urlBootstrapped: true,
    // W37b: same allow-list the api-server applies to X-User-Id on this
    // session (manager for 89608213 by default). The placeholder user id
    // is never on the list, so a param-less bootstrap stays sales_rep.
    role: roleForUserId(userId),
  };
}

function clampFraction(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_REFRESH_FRACTION;
  if (n <= 0) return 0.1;
  if (n >= 1) return 0.95;
  return n;
}

function noopTeardown(): void {
  /* no-op */
}

// --- Factory --------------------------------------------------------------

export function createHandshake(options: HandshakeOptions = {}): Handshake {
  const windowRef =
    options.windowRef ?? (typeof window !== 'undefined' ? window : null);
  const now = options.now ?? (() => Date.now());
  const refreshFraction = clampFraction(options.refreshFraction);
  const patterns = options.trustedOrigins ?? TRUSTED_ORIGIN_PATTERNS;
  const setT = options.setTimeout ?? ((fn, ms) => windowRef?.setTimeout(fn, ms));
  const clrT =
    options.clearTimeout ?? ((h) => windowRef?.clearTimeout(h as number));

  let state: HandshakeState = { status: 'pending', session: null, error: null };
  const listeners = new Set<HandshakeListener>();
  let refreshHandle: unknown = null;
  let expiryHandle: unknown = null;
  // W22: handle for the URL-bootstrap grace timer. Tracked here so a
  // real session arriving before the grace window expires can cancel it
  // and we do not accidentally shadow the real session later.
  let urlBootstrapHandle: unknown = null;
  // W23: handle for the redundant backup bootstrap timer at 3000ms.
  // Tracked so cancelTimers() cancels it when a real session or the
  // primary bootstrap arrives first, and so teardown() can cancel it.
  let urlBootstrapBackupHandle: unknown = null;
  let tornDown = false;

  function emit(next: HandshakeState): void {
    state = next;
    for (const l of listeners) {
      try {
        l(state);
      } catch {
        // Isolate subscriber errors.
      }
    }
  }

  // W27: swap the record context on the live session. Token, identity,
  // expiry and timers are untouched; only `context` (and optionally
  // `work`) change, so a url-bootstrap session keeps its bypass token and
  // a signed session keeps its JWT while the customer headers move.
  function setContext(
    context: HubSpotSessionContext,
    work?: HubSpotWorkContext | null,
  ): boolean {
    if (tornDown) return false;
    const current = state.session;
    if (!current) return false;
    if (!context || typeof context.objectId !== 'string' || context.objectId.length === 0) {
      return false;
    }
    const objectType =
      typeof context.objectType === 'string' && context.objectType.length > 0
        ? context.objectType
        : current.context.objectType;
    const nextWork = work === undefined ? current.work : work;
    const unchanged =
      current.context.objectId === context.objectId &&
      current.context.objectType === objectType &&
      (current.work?.workId ?? null) === (nextWork?.workId ?? null);
    if (unchanged) return true;
    emit({
      status: state.status,
      session: { ...current, context: { objectType, objectId: context.objectId }, work: nextWork },
      error: state.error,
    });
    return true;
  }

  function cancelTimers(): void {
    if (refreshHandle !== null) {
      try {
        clrT(refreshHandle);
      } catch {
        /* ignore */
      }
      refreshHandle = null;
    }
    if (expiryHandle !== null) {
      try {
        clrT(expiryHandle);
      } catch {
        /* ignore */
      }
      expiryHandle = null;
    }
    // W22: cancel the URL-bootstrap grace timer too, so a real
    // 'hubspot.session' that arrives inside the grace window wins and
    // the placeholder session never fires.
    if (urlBootstrapHandle !== null) {
      try {
        clrT(urlBootstrapHandle);
      } catch {
        /* ignore */
      }
      urlBootstrapHandle = null;
    }
    // W23: cancel the redundant backup bootstrap timer too.
    if (urlBootstrapBackupHandle !== null) {
      try {
        clrT(urlBootstrapBackupHandle);
      } catch {
        /* ignore */
      }
      urlBootstrapBackupHandle = null;
    }
  }

  function scheduleRefresh(session: HubSpotSession): void {
    cancelTimers();
    const lifetime = session.expiresAt - session.receivedAt;
    const refreshDelay = Math.max(
      MIN_REFRESH_DELAY_MS,
      Math.floor(lifetime * refreshFraction),
    );
    const expiryDelay = Math.max(0, session.expiresAt - now());
    refreshHandle = setT(() => {
      refreshHandle = null;
      if (tornDown) return;
      requestRefresh();
    }, refreshDelay);
    expiryHandle = setT(() => {
      expiryHandle = null;
      if (tornDown) return;
      // If the parent did not deliver a fresh session before expiresAt, we
      // flip to 'expired' and surface a banner. The current token is kept
      // so the UI can still render context behind the banner.
      if (state.status === 'ready' || state.status === 'refreshing') {
        emit({
          status: 'expired',
          session: state.session,
          error: {
            code: 'TOKEN_EXPIRED',
            message: 'HubSpot session expired. Re-open this card to continue.',
          },
        });
      }
    }, expiryDelay);
  }

  // W44: exchanged sessions cannot be refreshed by the parent (HubSpot's
  // openIframeModal never answers a postMessage), so only the expiry
  // timer is armed; at expiresAt the UI shows the re-open banner.
  function scheduleExpiryOnly(session: HubSpotSession): void {
    cancelTimers();
    const expiryDelay = Math.max(0, session.expiresAt - now());
    expiryHandle = setT(() => {
      expiryHandle = null;
      if (tornDown) return;
      if (state.status === 'ready' || state.status === 'refreshing') {
        emit({
          status: 'expired',
          session: state.session,
          error: {
            code: 'TOKEN_EXPIRED',
            message:
              'Workspace session expired. Close this window and open the Sales Workspace from the HubSpot contact record again.',
          },
        });
      }
    }, expiryDelay);
  }

  function requestRefresh(): void {
    // Flip to 'refreshing' but keep the current session visible.
    if (state.status === 'ready') {
      emit({ status: 'refreshing', session: state.session, error: null });
    }
    postToParent({ type: MSG.REFRESH, version: PROTOCOL_VERSION });
  }

  function postToParent(message: unknown): void {
    const w = windowRef;
    if (!w) return;
    const parent = w.parent;
    if (!parent || parent === w) return;
    try {
      // targetOrigin '*' is defensible here because the ancestor origin
      // varies with HubSpot's region/preview deployment. Inbound replies are
      // still gated by the origin + source check in handleMessage.
      parent.postMessage(message, '*');
    } catch {
      // Non-fatal: ignore.
    }
  }

  function handleMessage(event: MessageEvent): void {
    if (tornDown) return;
    const w = windowRef;
    if (!w) return;
    // CRITICAL: close the spoofing hole the scoping bot flagged. A nested
    // iframe on the same page can post the exact same message shape with a
    // crafted origin header - no, origin is set by the browser, but a sibling
    // iframe can still post with its own origin, and if we only checked
    // origin against a wildcard pattern we might trust the wrong frame. The
    // parent is the one we handshake with, period.
    if (event.source !== w.parent) return;
    if (!isTrustedOrigin(event.origin, patterns)) {
      emit({
        status: state.status === 'pending' ? 'error' : state.status,
        session: state.session,
        error: {
          code: 'HANDSHAKE_UNTRUSTED_ORIGIN',
          message: `Rejected message from untrusted origin ${event.origin}`,
        },
      });
      // Note: we do NOT remove the listener here. Spam from an untrusted
      // origin is logged (via error state) but the real parent is still
      // expected to deliver a valid session.
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    const type = (data as { type?: unknown }).type;
    if (type === MSG.SESSION) {
      const session = parseSessionPayload(data, now());
      if (!session) {
        emit({
          status: 'error',
          session: state.session,
          error: {
            code: 'HANDSHAKE_INVALID',
            message: 'hubspot.session arrived with missing or invalid fields',
          },
        });
        return;
      }
      emit({ status: 'ready', session, error: null });
      scheduleRefresh(session);
      return;
    }
    if (type === MSG.SESSION_ERROR) {
      const code = pickString(data as Record<string, unknown>, 'code');
      emit({
        status: 'expired',
        session: state.session,
        error: {
          code: 'REFRESH_FAILED',
          message: code
            ? `HubSpot refused the session refresh (${code}).`
            : 'HubSpot refused the session refresh.',
        },
      });
      return;
    }
  }

  // --- Bootstrap ---------------------------------------------------------

  if (!windowRef) {
    emit({
      status: 'error',
      session: null,
      error: {
        code: 'HANDSHAKE_INVALID',
        message: 'No window available to drive the handshake.',
      },
    });
    return {
      getState: () => state,
      subscribe: (l) => {
        listeners.add(l);
        try {
          l(state);
        } catch {
          /* ignore */
        }
        return () => listeners.delete(l);
      },
      teardown: () => {
        tornDown = true;
        cancelTimers();
        listeners.clear();
      },
      setContext,
    };
  }

  if (options.forceMock) {
    const mock = getMockHubSpotContext();
    if (!mock) {
      emit({
        status: 'error',
        session: null,
        error: {
          code: 'MOCK_UNAVAILABLE',
          message: 'VITE_DEV_MOCK_CONTEXT is set but no mock is available.',
        },
      });
    } else {
      const session: HubSpotSession = {
        token: mock.contextToken,
        expiresAt: now() + 60 * 60 * 1000,
        portalId: mock.portalId,
        userId: mock.userId,
        email: mock.userEmail,
        context: { objectType: 'contact', objectId: mock.objectId ?? '' },
        work: null,
        receivedAt: now(),
      };
      emit({ status: 'ready', session, error: null });
      scheduleRefresh(session);
    }
    return {
      getState: () => state,
      subscribe: (l) => {
        listeners.add(l);
        try {
          l(state);
        } catch {
          /* ignore */
        }
        return () => listeners.delete(l);
      },
      teardown: () => {
        tornDown = true;
        cancelTimers();
        listeners.clear();
      },
      setContext,
    };
  }

  windowRef.addEventListener('message', handleMessage);

  // Say hello to the parent so it knows we are mounted and listening. The
  // parent is expected to respond with 'hubspot.session'. If it never does,
  // the W22 URL-bootstrap grace timer below promotes URL query params to a
  // placeholder session so the UI mounts something useful; the message
  // listener STAYS attached and replaces that session in place if a real
  // one arrives later.
  postToParent({ type: MSG.HELLO, version: PROTOCOL_VERSION });
  w23log('factory.mounted', {
    hasParent: windowRef.parent !== windowRef,
    locationSearch: (() => {
      try {
        return windowRef.location?.search ?? '';
      } catch {
        return '(unreadable)';
      }
    })(),
    ancestorOrigins: (() => {
      try {
        const ao = (windowRef.location as unknown as { ancestorOrigins?: DOMStringList }).ancestorOrigins;
        return ao && typeof ao.length === 'number' ? Array.from({ length: ao.length }, (_, i) => ao.item(i)) : null;
      } catch {
        return '(unreadable)';
      }
    })(),
  });

  // W22 (2026-10-20): schedule the URL-bootstrap fallback. If the iframe
  // URL already carries portalId + contactId query params and no real
  // session arrives within urlBootstrapGraceMs, synthesize a session so
  // the UI renders instead of hanging on "Opening Sales Workspace...".
  // The grace window gives the real handshake a chance to win first; a
  // real 'hubspot.session' message that arrives before the timer fires
  // short-circuits the bootstrap because scheduleRefresh calls
  // cancelTimers() in handleMessage. A real message arriving AFTER the
  // bootstrap still wins because handleMessage emits the real session
  // and reschedules timers, replacing the url-bootstrap session.
  //
  // W23 (2026-10-20): the primary grace timer is now 500ms (down from
  // 1500ms) so a throttled sandbox iframe still fires within its
  // typical first-second budget. A redundant backup timer at 3000ms
  // re-runs the bootstrap unconditionally in case the primary was
  // deferred, and parseUrlBootstrapSession now always returns a
  // session (placeholders when URL params missing). The result: the
  // handshake can NEVER stay in 'pending' for more than ~3s, even if
  // every other signal fails.
  const graceMsRaw = options.urlBootstrapGraceMs;
  const graceMs = typeof graceMsRaw === 'number' && Number.isFinite(graceMsRaw) && graceMsRaw >= 0
    ? graceMsRaw
    : URL_BOOTSTRAP_GRACE_MS;
  const initialSearch = options.locationSearch
    ?? (() => {
      try {
        return windowRef.location?.search ?? '';
      } catch {
        return '';
      }
    })();
  // W44 (2026-10-21): Pattern A wins. When the iframe URL carries
  // ?s=<mint token> the legacy url-bootstrap timers are NEVER armed; the
  // exchange below either produces a real session or an error page.
  // The legacy path stays only for deep links that still carry
  // portalId/userId/email while the api-server's
  // WORKSPACE_LEGACY_BOOTSTRAP kill-switch is on.
  const mintToken = readMintTokenFromSearch(initialSearch);
  if (mintToken === null && graceMs !== Infinity) {
    const locSearch = initialSearch;
    const scheduleUrlBootstrap = (stage: string): void => {
      if (tornDown) {
        w23log('bootstrap.skip.tornDown', { stage });
        return;
      }
      if (state.status === 'ready' || state.status === 'refreshing') {
        w23log('bootstrap.skip.alreadyReady', { stage, status: state.status });
        return;
      }
      const session = parseUrlBootstrapSession(locSearch, now());
      w23log('bootstrap.emit.ready', { stage, portalId: session.portalId, objectId: session.context.objectId });
      emit({ status: 'ready', session, error: null });
      scheduleRefresh(session);
    };
    if (graceMs === 0) {
      scheduleUrlBootstrap('primary.inline');
    } else {
      w23log('bootstrap.schedule.primary', { graceMs });
      urlBootstrapHandle = setT(() => {
        urlBootstrapHandle = null;
        scheduleUrlBootstrap('primary.timer');
      }, graceMs);
    }
    // W23 belt-and-suspenders: second redundant timer, UNCONDITIONAL on
    // the primary firing. If a backgrounded sandbox iframe throttled the
    // primary setTimeout, or threw inside parseUrlBootstrapSession for
    // some reason, this backup window still mounts something after 3s.
    // scheduleRefresh/cancelTimers will no-op the backup if the primary
    // already fired because state.status will be 'ready' by then.
    if (graceMs !== 0) {
      w23log('bootstrap.schedule.backup', { graceMs: URL_BOOTSTRAP_BACKUP_GRACE_MS });
      urlBootstrapBackupHandle = setT(() => {
        urlBootstrapBackupHandle = null;
        scheduleUrlBootstrap('backup.timer');
      }, URL_BOOTSTRAP_BACKUP_GRACE_MS);
    }
  }

  if (mintToken !== null) {
    // Strip `s` from the address bar first so the token never survives
    // in history / referrers, then exchange it exactly once.
    stripMintTokenFromLocation(windowRef);
    const exchange = options.exchangeImpl ?? ((token: string) => exchangeMintToken({ token }));
    w23log('exchange.start', { tokenLength: mintToken.length });
    void exchange(mintToken).then(
      (result) => {
        if (tornDown) return;
        if (!result.ok) {
          w23log('exchange.failed', { errorCode: result.errorCode, status: result.status });
          emit({
            status: 'error',
            session: null,
            error: { code: 'SESSION_EXCHANGE_FAILED', message: exchangeFailureMessage(result) },
          });
          return;
        }
        const s = result.session;
        const session: HubSpotSession = {
          token: s.sessionToken,
          expiresAt: s.expiresAt,
          portalId: s.portalId,
          userId: s.userId,
          email: s.email,
          context: { objectType: 'contact', objectId: s.contactId },
          work: null,
          receivedAt: now(),
          role: isWorkspaceRole(s.role) ? s.role : roleForUserId(s.userId),
          sessionExchanged: true,
        };
        w23log('exchange.ready', { portalId: session.portalId, objectId: session.context.objectId, role: session.role });
        emit({ status: 'ready', session, error: null });
        scheduleExpiryOnly(session);
      },
      (err: unknown) => {
        if (tornDown) return;
        w23log('exchange.threw', { message: err instanceof Error ? err.message : String(err) });
        emit({
          status: 'error',
          session: null,
          error: {
            code: 'SESSION_EXCHANGE_FAILED',
            message: exchangeFailureMessage({ errorCode: 'NETWORK_ERROR', status: 0 }),
          },
        });
      },
    );
  }

  let teardownFn = (): void => {
    tornDown = true;
    cancelTimers();
    try {
      windowRef.removeEventListener('message', handleMessage);
    } catch {
      /* ignore */
    }
    listeners.clear();
  };

  return {
    getState: () => state,
    subscribe: (l) => {
      listeners.add(l);
      try {
        l(state);
      } catch {
        /* ignore */
      }
      return () => listeners.delete(l);
    },
    teardown: () => {
      teardownFn();
      teardownFn = noopTeardown;
    },
    setContext,
  };
}

// Test-only exports. Public callers should not depend on these.
export const __test__ = {
  isTrustedOrigin,
  parseSessionPayload,
  parseUrlBootstrapSession,
  TRUSTED_ORIGIN_PATTERNS,
  MIN_REFRESH_DELAY_MS,
  URL_BOOTSTRAP_GRACE_MS,
  URL_BOOTSTRAP_BACKUP_GRACE_MS,
  URL_BOOTSTRAP_TOKEN,
  URL_BOOTSTRAP_PLACEHOLDER_PORTAL,
};
